import { useAlerts } from '@SBGSports/referee-react';
import axios from 'axios';
import { useIframeListener } from 'iframing';
import { Configuration, User, UserApi } from 'oas';
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export interface AuthValues {
    csrfAuthd: boolean;
    indeterminate: boolean;
    user?: User;
    login: (username: string, password: string) => Promise<void>;
    loginWithToken: (token: string) => Promise<void>;
    loginWithSharedToken: (token: string) => Promise<string>;
    verifyMfa: (code: string) => Promise<void>;
    updateEmailPassword: (
        email: string,
        password: string,
        password2: string,
        password_strength: string,
    ) => Promise<void>;
    logout: () => void;
    checkAuthentication: () => void;
    reset: () => void;
    reloadUser: () => void;
    isAuthenticated: boolean;
    getAccessToken: () => Promise<string>;
}

const AuthContext = React.createContext<AuthValues | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useProvideAuth();
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthValues => {
    const context = React.useContext(AuthContext);
    const checked = React.useRef(false);

    if (context === undefined) {
        throw new Error('useAuth must be used within AuthProvider');
    }

    React.useEffect(() => {
        if (checked.current) {
            return;
        }

        checked.current = true;

        if (context.indeterminate) {
            context.checkAuthentication();
        }
    }, [context]);

    return context;
};

// https://stackoverflow.com/a/25490531
export function getCookie(name: string) {
    return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
}

// based on https://stackoverflow.com/a/51313011
export function setCookie(name: string, value: string, days: number, secureCookie?: boolean) {
    let expires = '';
    let secure = '';

    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
    }

    if (secureCookie) {
        secure = '; secure';
    }

    document.cookie = name + '=' + (value || '') + expires + secure + '; path=/';
}

const fetchUser = async (apiConfig: Configuration) => {
    const meApi = new UserApi(apiConfig);
    const { data: newUser } = await meApi.getMe(['permissions']);

    return newUser;
};

const useProvideAuth = (): AuthValues => {
    const [csrfAuthd, setCsrfAuthd] = React.useState(false);
    const [indeterminate, setIndeterminate] = React.useState<boolean>(true);
    const [user, setUser] = React.useState<User | undefined>();
    const [needsAuthCheck, setNeedsAuthCheck] = React.useState<boolean>(false);
    const apiConfig = React.useMemo(() => new Configuration({ basePath: process.env.REACT_APP_API_HOST + '/v6' }), []);
    const iframeUnauthenticatedEvent = useIframeListener('__authentication_unauthenticated');
    const iframeReauthenticateEvent = useIframeListener('__authentication_reauthenticate');
    const queryClient = useQueryClient();
    const [accessToken, setAccessToken] = React.useState({ token: undefined, expiry: 0 });

    const getAccessToken = async (): Promise<string> => {
        if (Date.now() / 1000 > accessToken.expiry || accessToken.token === undefined) {
            const token = await refreshAccessToken();
            return token;
        }

        return accessToken.token;
    };

    const refreshAccessToken = async (): Promise<string> => {
        // this has to use fetch, using axios.post causes (not always, but often) the subsequent requests to return a 401 and therefore initiates  a logout
        const req = await fetch(`${process.env.REACT_APP_API_HOST}/v6/oauth/refresh`, {
            method: 'POST',
            body: JSON.stringify({
                grant_type: 'refresh_token',
                refresh_token: getCookie('auth_refresh_token'),
                client_id: process.env.REACT_APP_CLIENT_ID,
                client_secret: process.env.REACT_APP_CLIENT_SECRET,
            }),
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
        });

        const res = await req.json();

        const tokenInfo = {
            token: res.access_token,
            expiry: res.expires_at,
        };

        setCookie('auth_refresh_token', res.refresh_token, 7, process.env.REACT_APP_ENVIRONMENT !== 'dev');
        setAccessToken(tokenInfo);

        return res.access_token;
    };

    // Trigger a fetch to /me to see if we are authenticated with the backend
    const checkAuthentication = () => {
        setNeedsAuthCheck(true);
    };

    // Login using username and password
    const login: AuthValues['login'] = async (username, password) => {
        reset();
        try {
            const clientId = process.env.REACT_APP_CLIENT_ID;
            const clientSecret = process.env.REACT_APP_CLIENT_SECRET;

            await axios.post(`${process.env.REACT_APP_AUTH_HOST}/login`, {
                name: username,
                password,
            });

            // when using a remote backend from localhost, this call will cause the following request to return a 401, thus immediately causing a logout after logging in
            if (process.env.REACT_APP_ENVIRONMENT !== 'dev') {
                const res = await axios.post(`${process.env.REACT_APP_API_HOST}/v6/oauth/token`, {
                    username: username,
                    password,
                    grant_type: 'password',
                    client_id: clientId,
                    client_secret: clientSecret,
                });

                const tokenInfo = {
                    token: res.data.access_token,
                    expiry: res.data.expires_at,
                };

                setCookie('auth_refresh_token', res.data.refresh_token, 7, process.env.REACT_APP_ENVIRONMENT !== 'dev');
                setAccessToken(tokenInfo);
            }
        } catch (error) {
            // If csrf token mismatch error, refetch the csrf token
            if (axios.isAxiosError(error) && error.response?.status === 419) {
                setCsrfAuthd(false);
            }

            throw error;
        }

        setNeedsAuthCheck(true);
    };

    // Login using a JWT
    const loginWithToken: AuthValues['loginWithToken'] = async (token) => {
        reset();
        await axios.post(`${process.env.REACT_APP_AUTH_HOST}/auth/registration/login`, { token });

        setNeedsAuthCheck(true);
    };

    // Login using a shareable JWT
    const loginWithSharedToken: AuthValues['loginWithSharedToken'] = async (token) => {
        reset();
        const res = await axios.post(`${process.env.REACT_APP_AUTH_HOST}/auth/share`, { token });

        setNeedsAuthCheck(true);
        return res.data;
    };

    const verifyMfa: AuthValues['verifyMfa'] = async (code) => {
        await axios.post(`${process.env.REACT_APP_AUTH_HOST}/loginSecondStep`, {
            userId: user?.id,
            verificationCode: code,
        });

        setUser(undefined);
        setNeedsAuthCheck(true);
    };

    const updateEmailPassword: AuthValues['updateEmailPassword'] = async (
        email,
        password,
        password2,
        password_strength,
    ) => {
        await axios.post(`${process.env.REACT_APP_AUTH_HOST}/registration/update`, {
            email,
            password,
            password2,
            password_strength,
        });

        setUser(undefined);
        setNeedsAuthCheck(true);
    };

    // Reset client-side auth
    const reset = React.useCallback((): void => {
        setUser(undefined);
        setNeedsAuthCheck(false);
        queryClient.clear();
    }, [queryClient]);

    const reloadUser = React.useCallback(() => {
        (async () => {
            setUser(await fetchUser(apiConfig));
        })();
    }, [apiConfig]);

    // Send to logout page
    const logout = React.useCallback(() => {
        setCookie('auth_refresh_token', '', 7, process.env.REACT_APP_ENVIRONMENT !== 'dev');
        setAccessToken({ token: undefined, expiry: 0 });
        window.location.href = '/logout';
    }, []);

    const { showAlert, closeAlert } = useAlerts();
    const isSnackbarDisplayed = React.useRef(false);
    const snackbarRef = React.useRef<string>();

    // Get csrf-cookie
    useQuery({
        queryKey: ['csrf-cookie'],

        queryFn: async () => {
            const response = await axios.get(process.env.REACT_APP_AUTH_HOST + '/sanctum/csrf-cookie');
            setCsrfAuthd(true);
            snackbarRef.current && closeAlert(snackbarRef.current);
            isSnackbarDisplayed.current = false;
            return response;
        },

        staleTime: 3600 * 8 * 1000,
        enabled: !csrfAuthd,

        retry: (failureCount) => {
            if (!isSnackbarDisplayed.current) {
                snackbarRef.current = showAlert('Connection error, retrying...', '', { variant: 'alert' });
                isSnackbarDisplayed.current = true;
            }

            return failureCount < 100;
        },
    });

    // Fetch user
    React.useEffect(() => {
        if (!csrfAuthd || !needsAuthCheck || user) {
            return;
        }

        (async () => {
            setIndeterminate(true);

            try {
                setUser(await fetchUser(apiConfig));

                setNeedsAuthCheck(false);
            } catch (error) {
                reset();
            }

            setIndeterminate(false);
        })();
    }, [apiConfig, csrfAuthd, needsAuthCheck, reset, user]);

    // Logout when iframe 'unauthenticated' event is received
    React.useEffect(() => {
        if (iframeUnauthenticatedEvent !== undefined) {
            logout();
        }
    }, [iframeUnauthenticatedEvent, logout]);

    // Reload user when iframe 'reauthenticate' event is received
    React.useEffect(() => {
        if (iframeReauthenticateEvent !== undefined) {
            setUser(undefined);
            setNeedsAuthCheck(true);
        }
    }, [iframeReauthenticateEvent]);

    return {
        csrfAuthd,
        indeterminate,
        user,
        login,
        loginWithToken,
        loginWithSharedToken,
        verifyMfa,
        updateEmailPassword,
        logout,
        checkAuthentication,
        reset,
        reloadUser,
        isAuthenticated: !!user && !user.require_mfa && !user.needs_password_update,
        getAccessToken,
    };
};
